var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useMemo, useState } from "react";
import Table from "../Table";
import Pagination from "./Pagination/Pagination";
import TextSkeleton from "../../Skeleton/TextSkeleton";
import { useSearchParams } from "react-router-dom";
import Checkbox from "../../Input/Checkbox/Checkbox";
import BatchActions from "./BatchActions";
import SearchControl from "./SearchControl";
import Toolbar from "./Toolbar";
import { isEmpty } from "../../Util";
var retrieveData = function (pageRequest, columns) {
    var useSearch = pageRequest.search !== undefined
        && pageRequest.search.query !== undefined
        && !isEmpty(pageRequest.search.query);
    var requestUrl = new URL(useSearch && pageRequest.search !== undefined && pageRequest.search.query !== undefined
        ? pageRequest.search.baseUrl
        : pageRequest.baseUrl);
    requestUrl.searchParams.set("size", pageRequest.pageSize + "");
    requestUrl.searchParams.set("page", pageRequest.pageNumber + "");
    if (useSearch && pageRequest.search !== undefined && pageRequest.search.query !== undefined) {
        requestUrl.searchParams.set(pageRequest.search.urlParameter, pageRequest.search.query);
    }
    else {
        var sortedColumns = columns.filter(function (col) { return col.sortable && col.sorted !== undefined; });
        if (sortedColumns.length !== 0) {
            var sortedColumn = sortedColumns[0];
            requestUrl.searchParams.set("sort", "".concat(sortedColumn.dataName, ",").concat(sortedColumn.sorted));
        }
    }
    return pageRequest.axios.get(requestUrl.toString()).then(function (response) {
        return response.data;
    });
};
var DataTable = function (_a) {
    var initialPageRequest = _a.initialPageRequest, columns = _a.columns, onRowClick = _a.onRowClick, summaryFormatter = _a.summaryFormatter, messages = _a.messages, emptyState = _a.emptyState, _b = _a.withUrlPageParameter, withUrlPageParameter = _b === void 0 ? false : _b, _c = _a.urlPageParameterName, urlPageParameterName = _c === void 0 ? "page" : _c, _d = _a.scrollToTopOnPageChange, scrollToTopOnPageChange = _d === void 0 ? false : _d, className = _a.className, _e = _a.compactPagination, compactPagination = _e === void 0 ? false : _e, _f = _a.alwaysShowToolbar, alwaysShowToolbar = _f === void 0 ? false : _f, baseUrlChangedCallback = _a.baseUrlChangedCallback, initialLastUpdated = _a.initialLastUpdated, batchActions = _a.batchActions;
    var _g = useSearchParams(), searchParams = _g[0], setSearchParams = _g[1];
    var _h = useState(null), data = _h[0], setData = _h[1];
    var _j = useState(initialPageRequest), pageRequest = _j[0], setPageRequest = _j[1];
    var _k = useState(columns), cols = _k[0], setCols = _k[1];
    var _l = useState(false), loading = _l[0], setLoading = _l[1];
    var _m = useState(null), lastRowClickIndex = _m[0], setLastRowClickIndex = _m[1];
    var _o = useState(initialLastUpdated), lastUpdated = _o[0], setLastUpdated = _o[1];
    var dataTableId = useId();
    var refreshOnBaseUrlChange = function () {
        var _a;
        var baseUrlUpdated = false;
        var searchBaseUrlUpdated = false;
        var baseUrl = pageRequest.baseUrl;
        var searchBaseUrl = (_a = pageRequest.search) === null || _a === void 0 ? void 0 : _a.baseUrl;
        if (pageRequest.baseUrl !== initialPageRequest.baseUrl) {
            baseUrlUpdated = true;
            baseUrl = initialPageRequest.baseUrl;
        }
        if (pageRequest.search !== undefined
            && initialPageRequest.search !== undefined
            && pageRequest.search.baseUrl !== initialPageRequest.search.baseUrl) {
            searchBaseUrlUpdated = true;
            searchBaseUrl = initialPageRequest.search.baseUrl;
        }
        if (baseUrlUpdated || searchBaseUrlUpdated) {
            var newPageRequest = __assign(__assign({}, pageRequest), { pageNumber: 0 });
            if (baseUrlUpdated) {
                newPageRequest.baseUrl = baseUrl;
            }
            if (searchBaseUrlUpdated && newPageRequest.search !== undefined && searchBaseUrl !== undefined) {
                newPageRequest.search.baseUrl = searchBaseUrl;
            }
            setPageRequest(newPageRequest);
        }
    };
    refreshOnBaseUrlChange();
    if (initialLastUpdated !== lastUpdated) {
        setLastUpdated(initialLastUpdated);
    }
    useEffect(function () {
        if (!loading) {
            return;
        }
        if (withUrlPageParameter) {
            var pageBefore = searchParams.get(urlPageParameterName);
            pageRequest.pageNumber === 0
                ? searchParams.delete(urlPageParameterName)
                : searchParams.set(urlPageParameterName, (pageRequest.pageNumber + 1) + "");
            if (pageBefore !== searchParams.get(urlPageParameterName)) {
                setSearchParams(searchParams, {});
            }
        }
        retrieveData(pageRequest, cols).then(function (page) {
            page.content.map(function (v) { return v._selected = false; });
            setData(page);
            setLoading(false);
            if (scrollToTopOnPageChange) {
                window.scrollTo(0, 0);
            }
            if (lastRowClickIndex !== null && baseUrlChangedCallback) {
                baseUrlChangedCallback(page.content[lastRowClickIndex]);
            }
        });
    }, [loading]);
    useEffect(function () {
        setLoading(true);
    }, [pageRequest, lastUpdated]);
    useEffect(function () {
        if (!withUrlPageParameter) {
            return;
        }
        // Used for history (going back)
        var pageNumber = searchParams.get(urlPageParameterName);
        if (pageNumber === null && pageRequest.pageNumber !== null) {
            setPageRequest(__assign(__assign({}, pageRequest), { pageNumber: 0 }));
        }
        else if (pageNumber !== null && (parseInt(pageNumber) - 1) !== pageRequest.pageNumber) {
            setPageRequest(__assign(__assign({}, pageRequest), { pageNumber: parseInt(pageNumber) - 1 }));
        }
    }, [searchParams.get(urlPageParameterName)]);
    useEffect(function () {
        if (withUrlPageParameter) {
            // Respond to initial page parameter
            var initialPage = searchParams.get(urlPageParameterName);
            if (initialPage !== null) {
                pageRequest.pageNumber = parseInt(initialPage) - 1;
            }
        }
        setCols(cols.map(function (col) {
            if (col.sortable) {
                col.onClick = function () { return sortByCol(col); };
            }
            return col;
        }));
    }, []);
    var sortByCol = function (col) {
        var newCols = cols.map(function (c) {
            if (c.dataName !== col.dataName) {
                c.sorted = undefined;
                return c;
            }
            c.sorted = c.sorted === undefined
                ? "desc"
                : (c.sorted === "desc" ? c.sorted = "asc" : c.sorted = "desc");
            return c;
        });
        setCols(newCols);
        setPageRequest(__assign(__assign({}, pageRequest), { pageNumber: 0 }));
    };
    var processedData = useMemo(function () {
        return data && !loading
            ? data.content.map(function (d, i) {
                var mappedData = {};
                cols.map(function (col) {
                    var rawValue = d[col.dataName || col.name];
                    return mappedData[col.name] = col.formatter ? col.formatter(rawValue, d) : rawValue;
                });
                if (batchActions) {
                    mappedData["_selected"] =
                        _jsx(Checkbox, { id: "row-check-".concat(i, "_").concat(dataTableId), displayName: batchActions.selectRowText, checked: data.content[i]._selected, onChange: function (e) {
                                var newData = data;
                                newData.content[i] = __assign(__assign({}, d), { _selected: e.target.checked });
                                setData(__assign({}, newData));
                            }, onClick: function (e) { return e.stopPropagation(); } }, i);
                }
                return mappedData;
            })
            : __spreadArray([], Array(pageRequest.pageSize), true).map(function (_) {
                var mappedData = {};
                cols.map(function (col, index) {
                    return mappedData[col.name] = _jsx(TextSkeleton, { width: "max-w-[50px]" }, index);
                });
                return mappedData;
            });
    }, [loading, data, columns]);
    return (_jsxs("div", { children: [_jsxs(Toolbar, { children: [data && batchActions
                        ? _jsx(BatchActions, { dropdownButtonText: batchActions.dropdownButtonText, batchActions: batchActions.actions, data: data, setData: setData, alwaysShowToolbar: alwaysShowToolbar })
                        : _jsx(_Fragment, {}), _jsx(SearchControl, { pageRequest: pageRequest, setPageRequest: setPageRequest })] }), _jsx(Table, { sortingDisabled: pageRequest.search !== undefined && pageRequest.search.query !== undefined, columns: __spreadArray(__spreadArray([], (batchActions ? [{
                        name: "_selected",
                        sortable: false,
                        thClassName: "w-4",
                        altTh: _jsx(Checkbox, { id: "_data_table_select_toggle_".concat(dataTableId), displayName: batchActions.toggleSelectAllText, checked: data === null ? false : data.content.filter(function (d) { return d._selected; }).length === data.content.length || false, onChange: function (e) {
                                if (!data || loading) {
                                    return;
                                }
                                var newContent = data.content.map(function (d) { return (__assign(__assign({}, d), { _selected: e.target.checked })); });
                                setData(__assign(__assign({}, data), { content: newContent }));
                            } })
                    }] : []), true), cols, true), data: processedData, emptyState: emptyState, footer: data && data.numberOfElements > 0
                    ? _jsx(Pagination, { summaryFormatter: summaryFormatter, page: data, pageRequest: pageRequest, setPageRequest: setPageRequest, messages: messages, compactPagination: compactPagination })
                    : undefined, onRowClick: onRowClick && (function (formattedTableData, index) {
                    if (onRowClick) {
                        onRowClick(formattedTableData, index, data === null || data === void 0 ? void 0 : data.content[index]);
                        setLastRowClickIndex(index);
                    }
                }), className: className })] }));
};
export default DataTable;
